/**
 * useGameState hook
 * Game logic delegated to GameController for CPU mode.
 * UI state and animation staging remain in this hook.
 *
 * Strategy: refactor staged — GC used for pure state transforms,
 * hook handles all React state and visual staging.
 */

import { useState, useCallback, useRef, useEffect, useMemo, Dispatch, SetStateAction } from 'react';
import { Card, Player, GameState } from '../types';
import {
  createDeck,
  shuffle,
  getPossibleCaptures,
  getHandSum,
  createPlayers
} from '../lib/gameUtils';

import { GAME_CONSTANTS } from '../lib/constants';
import { createGameController, GameController } from '../lib/GameController';

export interface GameStateHook {
  // === CORE GAME STATE ===
  gameState: GameState | null;
  setGameState: Dispatch<SetStateAction<GameState | null>>;

  // Card selection state
  selectedCard: Card | null;
  setSelectedCard: Dispatch<SetStateAction<Card | null>>;
  hoveredCardId: string | null;
  setHoveredCardId: Dispatch<SetStateAction<string | null>>;

  // Capture state
  possibleCaptures: Card[][];
  setPossibleCaptures: Dispatch<SetStateAction<Card[][]>>;
  showCapturesMenu: boolean;
  setShowCapturesMenu: Dispatch<SetStateAction<boolean>>;
  capturingCards: { cards: Card[], playedCard: Card, playerIndex: number } | null;
  setCapturingCards: Dispatch<SetStateAction<{ cards: Card[], playedCard: Card, playerIndex: number } | null>>;

  // Sbarazzo reveal state
  revealedCardIds: Set<string>;
  setRevealedCardIds: Dispatch<SetStateAction<Set<string>>>;
  revealingCardIds: Set<string>;
  setRevealingCardIds: Dispatch<SetStateAction<Set<string>>>;

  // Deal animation trigger
  dealId: number;
  setDealId: Dispatch<SetStateAction<number>>;

  // Error message (UI)
  errorMessage: string | null;
  setErrorMessage: Dispatch<SetStateAction<string | null>>;

  // === GAME LOGIC FUNCTIONS ===
  startGame: (numPlayers: number) => void;
  restartGame: () => void;
  handleNextRound: () => void;
  handleLowHandDeclaration: (playerIndex: number) => void;
  handleThreeAcesDeclaration: (playerIndex: number) => void;
  performCapture: (playerIndex: number, playedCard: Card, capturedCards: Card[]) => void;
  playCard: (card: Card) => void;

  // === SIDE EFFECTS (callbacks that use gameState internally) ===
  getPossibleCapturesForCard: (card: Card) => Card[][];

  // === STATE REFS ===
  scopaParticleSeeds: { x: number; y: number; scale: number; rotate: number; duration: number }[];
}

export function useGameState(): GameStateHook {
  // === CORE GAME STATE ===
  const [gameState, setGameState] = useState<GameState | null>(null);
  const gameStateRef = useRef<GameState | null>(null);
  useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null);
  const [possibleCaptures, setPossibleCaptures] = useState<Card[][]>([]);
  const [showCapturesMenu, setShowCapturesMenu] = useState(false);
  const [capturingCards, setCapturingCards] = useState<{ cards: Card[], playedCard: Card, playerIndex: number } | null>(null);

  // Sbarazzo reveal state
  const [revealedCardIds, setRevealedCardIds] = useState<Set<string>>(new Set());
  const [revealingCardIds, setRevealingCardIds] = useState<Set<string>>(new Set());

  // Deal animation trigger
  const [dealId, setDealId] = useState(0);

  // Error message
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Mount tracker — prevents timeouts from firing after unmount
  const isActiveRef = useRef(true);
  useEffect(() => {
    isActiveRef.current = true;
    return () => { isActiveRef.current = false; };
  }, []);

  // === GAME CONTROLLER (synced to gameState) ===
  const gcRef = useRef<GameController | null>(null);

  // Sync GC to gameState: recreate GC whenever gameState changes
  useEffect(() => {
    if (gameState) {
      gcRef.current = createGameController(gameState);
    }
  }, [gameState]);

  // Stable seed cache for scopa particles — useMemo avoids side-effect in render body
  const scopaParticleSeeds = useMemo(() =>
    Array.from({ length: 40 }, () => ({
      x: (Math.random() - 0.5) * 0.8,
      y: (Math.random() - 0.5) * 0.8,
      scale: Math.random() * 0.5 + 0.5,
      rotate: Math.random() * 360,
      duration: 1.5 + Math.random()
    })),
    []
  );

  // === HELPER: getPossibleCapturesForCard (delegated to GC) ===
  const getPossibleCapturesForCard = useCallback((card: Card): Card[][] => {
    if (!gcRef.current) return [];
    return gcRef.current.getPossibleCapturesForCard(card);
  }, []);

  // === HANDLE NEXT ROUND (delegated to GC) ===
  const handleNextRound = useCallback(() => {
    if (!gcRef.current) return;
    const newState = gcRef.current.handleNextRound();
    
    // If player 0 has a low hand or three aces, give them the turn immediately to declare
    const p0 = newState.players[0];
    const p0Sum = getHandSum(p0.hand);
    const hasThreeAces = p0.hand.length === 3 && p0.hand.every(c => c.value === 1);
    const hasLowHand = p0.hand.length === 3 && p0Sum <= GAME_CONSTANTS.SBARAZZO_DECLARE_THRESHOLD;
    
    if (hasLowHand || hasThreeAces) {
      setGameState({
        ...newState,
        currentPlayerIndex: 0,
      });
    } else {
      setGameState(newState);
    }

    // Dispatch round_completed event for VideoAdModal trigger B
    window.dispatchEvent(new Event('round_completed'));
  }, []);

  // === START GAME ===
  const startGame = useCallback((numPlayers: number) => {
    try {
      const deck = shuffle(createDeck());
      const table = deck.splice(0, 4);
      const players = createPlayers(deck, numPlayers);

      const newState: GameState = {
        deck,
        table,
        players,
        currentPlayerIndex: 0,
        numPlayers,
        lastToCapture: null,
        lastPlayedCard: null,
        phase: 'playing',
        messages: ['Inizia la partita!'],
        roundStartPlayer: 0,
      };

      setGameState(newState);
      setSelectedCard(null);
      setPossibleCaptures([]);
      setShowCapturesMenu(false);
      setDealId(1);
    } catch (e) {
      console.error('[startGame] error:', e);
    }
  }, []);

  // === RESTART GAME ===
  const restartGame = useCallback(() => {
    const deck = shuffle(createDeck());
    const table = deck.splice(0, 4);

    const numPlayers = gameState?.numPlayers ?? 2;
    const players = createPlayers(deck, numPlayers);

    const newState: GameState = {
      deck,
      table,
      players,
      currentPlayerIndex: 0,
      numPlayers,
      lastToCapture: null,
      lastPlayedCard: null,
      phase: 'playing',
      messages: ['Nuova partita!'],
      roundStartPlayer: 0,
    };

    setGameState(newState);
    setSelectedCard(null);
    setPossibleCaptures([]);
    setShowCapturesMenu(false);
    setDealId(d => d + 1);
    setRevealedCardIds(new Set());
  }, [gameState?.numPlayers]);

  // === HANDLE LOW HAND DECLARATION (SBARAZZO) ===
  const handleLowHandDeclaration = useCallback((playerIndex: number) => {
    const currentGameState = gameStateRef.current;
    if (!currentGameState) return;

    const player = currentGameState.players[playerIndex];
    const cardIds = new Set(player.hand.map(c => c.id));
    setRevealingCardIds(cardIds);

    setTimeout(() => {
      if (!isActiveRef.current) return;
      setRevealingCardIds(new Set());
      setRevealedCardIds(prev => new Set([...Array.from(prev), ...Array.from(cardIds)]));
    }, 500);

    setGameState(prev => {
      if (!prev) return null;
      const p = prev.players[playerIndex];
      const sum = getHandSum(p.hand);
      if (sum <= GAME_CONSTANTS.SBARAZZO_DECLARE_THRESHOLD && !p.hasDeclaredLowHand) {
        const newPlayers = prev.players.map((pl, i) => i === playerIndex ? { 
          ...pl, 
          hasDeclaredLowHand: true,
          lowHandCount: (pl.lowHandCount || 0) + 1
        } : pl);
        return {
          ...prev,
          players: newPlayers,
          messages: [`${p.name} dichiara sbarazzo (somma ${sum})! +3 punti.`, ...prev.messages.slice(0, 4)],
          lowHandEvent: { playerIndex, timestamp: Date.now() },
        };
      }
      return prev;
    });
    setTimeout(() => {
      if (!isActiveRef.current) return;
      setGameState(curr => curr ? { ...curr, lowHandEvent: null } : null);
    }, 2500);
  }, []);

  // === HANDLE THREE ACES DECLARATION ===
  const handleThreeAcesDeclaration = useCallback((playerIndex: number) => {
    const currentGameState = gameStateRef.current;
    if (!currentGameState) return;

    const player = currentGameState.players[playerIndex];
    const aces = player.hand.filter(c => c.value === 1);
    if (aces.length !== 3) return;

    const cardIds = new Set(player.hand.map(c => c.id));
    setRevealingCardIds(cardIds);

    setTimeout(() => {
      if (!isActiveRef.current) return;
      setRevealingCardIds(new Set());
      setRevealedCardIds(prev => new Set([...Array.from(prev), ...Array.from(cardIds)]));
    }, 500);

    setGameState(prev => {
      if (!prev) return null;
      const p = prev.players[playerIndex];
      const a = p.hand.filter(c => c.value === 1);
      if (a.length === 3 && !p.hasDeclaredThreeAces) {
        const newPlayers = prev.players.map((pl, i) => i === playerIndex ? { 
          ...pl, 
          hasDeclaredThreeAces: true,
          threeAcesCount: (pl.threeAcesCount || 0) + 1
        } : pl);
        return {
          ...prev,
          players: newPlayers,
          messages: [`${p.name} dichiara tre assi! +6 punti.`, ...prev.messages.slice(0, 4)],
        };
      }
      return prev;
    });
  }, []);

  // === PERFORM CAPTURE ===
  // Animation staging stays in hook; innermost mutation delegated to GC.
  const performCapture = useCallback((playerIndex: number, playedCard: Card, capturedCards: Card[]) => {
    // Stage 1: Play the card (visual)
    setGameState(prev => {
      if (!prev) return null;
      const newPlayers = prev.players.map((p, i) => i === playerIndex ? {
        ...p,
        hand: p.hand.filter(c => c.id !== playedCard.id)
      } : p);
      return {
        ...prev,
        players: newPlayers,
        lastPlayedCard: { card: playedCard, playerIndex }
      };
    });

    // Stage 2: Execute actual capture after short delay
    setTimeout(() => {
      if (!isActiveRef.current) return;
      setCapturingCards({ cards: capturedCards, playedCard, playerIndex });

      // Stage 3: Apply capture (GC mutation) after animation
      setTimeout(() => {
        if (!isActiveRef.current) return;
        if (gcRef.current) {
          const newState = gcRef.current.performCapture(playerIndex, playedCard, capturedCards);
          setGameState(newState);

          // Auto-clear scopa event after animation
          if (newState.scopaEvent) {
            setTimeout(() => {
              if (!isActiveRef.current) return;
              setGameState(curr => curr ? { ...curr, scopaEvent: null } : null);
            }, 2500);
          }
        }
        setCapturingCards(null);
      }, 1000); // Extended to 1000ms for staggered flying animation
    }, 800);

    setSelectedCard(null);
    setPossibleCaptures([]);
    setShowCapturesMenu(false);
  }, []);

  // === PLAY CARD ===
  const playCard = useCallback((card: Card) => {
    if (!gameState) return;
    if (gameState.currentPlayerIndex !== 0) {
      setErrorMessage("Non è il tuo turno!");
      setTimeout(() => { if (isActiveRef.current) setErrorMessage(null); }, 2000);
      return;
    }
    if (gameState.lastPlayedCard) return;

    const captures = getPossibleCaptures(card, gameState.table);

    if (captures.length === 0) {
      // No capture: visual play first, then GC mutation
      setGameState(prev => {
        if (!prev) return null;
        const newPlayers = prev.players.map((p, i) => i === 0 ? {
          ...p,
          hand: p.hand.filter(c => c.id !== card.id)
        } : p);
        return {
          ...prev,
          players: newPlayers,
          lastPlayedCard: { card, playerIndex: 0 }
        };
      });

      setTimeout(() => {
        if (!isActiveRef.current) return;
        if (gcRef.current) {
          const newState = gcRef.current.playCard(card);
          setGameState(newState);
        }
      }, 800);
      setSelectedCard(null);
    } else if (captures.length === 1) {
      performCapture(0, card, captures[0]);
    } else {
      setPossibleCaptures(captures);
      setSelectedCard(card);
      setShowCapturesMenu(true);
    }
  }, [gameState, performCapture]);

  return {
    // Core game state
    gameState,
    setGameState,
    selectedCard,
    setSelectedCard,
    hoveredCardId,
    setHoveredCardId,
    possibleCaptures,
    setPossibleCaptures,
    showCapturesMenu,
    setShowCapturesMenu,
    capturingCards,
    setCapturingCards,
    revealedCardIds,
    setRevealedCardIds,
    revealingCardIds,
    setRevealingCardIds,
    dealId,
    setDealId,
    errorMessage,
    setErrorMessage,

    // Game logic functions
    startGame,
    restartGame,
    handleNextRound,
    handleLowHandDeclaration,
    handleThreeAcesDeclaration,
    performCapture,
    playCard,

    // Helpers
    getPossibleCapturesForCard,

    // Refs
    scopaParticleSeeds,
  };
}